import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { Subscription } from 'rxjs';

import { CommonValues } from '../../classes/common-values';

@Component({
  selector: 'nsc-stepper',
  templateUrl: './stepper.component.html',
  styleUrls: ['./stepper.component.scss'],
  standalone: false
})
export class StepperComponent implements OnChanges, OnInit, AfterViewInit, OnDestroy {
  @Input('route') route: string;

  @ViewChild('stepper')  myStepper: MatStepper;

  // Keeps the stepper's selectionChange subscription so we can dispose it in
  // ngOnDestroy and avoid a memory leak if the component is recreated.
  private stepperSelectionSub?: Subscription;

  // Bound to <mat-horizontal-stepper [linear]>. Declared explicitly (was
  // referenced in the template without a declaration) but kept as `false`
  // to preserve the production runtime — the field was previously
  // undefined, which Material coerces to non-linear stepper mode.
  // Do NOT flip to true without a functional impact review: it would
  // block users from moving past incomplete steps.
  isLinear = false;

  // Static labels — kept in sync with the mat-step entries in the template.
  // Used to build the screen-reader announcement below (WCAG 4.1.3).
  private readonly stepTitles: string[] = [
    'Enter Personal Information',
    'Select Transcript and Delivery Details',
    'Confirm Order and Checkout'
  ];

  // Text bound to a visually-hidden aria-live region in the template.
  // Updated whenever the stepper advances/retreats so screen-reader users
  // are notified of progress even though the header UI is non-interactive.
  currentStepAnnouncement = '';

  content = {
    step1StatusComplete: false,
    step2StatusComplete: false,
    step3StatusComplete: false,

    state: null
  };

  show = {
    stepper: null
  };

  values = {
    step1Routes: [
      this.commonValues.routes.schoolWelcome
    ],
    step2Routes: [
      this.commonValues.routes.recipientSelect
    ],
    step3Routes: [
      this.commonValues.routes.orderReview
    ]
  };

  constructor(private commonValues: CommonValues) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    this.setStepperConfig(this.route);
    this.setStepperVisibility(this.route);
    this.updateStepAnnouncement();
  }

  // ngOnChanges fires before @ViewChild('stepper') resolves, so the initial
  // announcement built there is always empty. Re-run the announcement once
  // the view is initialized and subscribe to `selectionChange` so any
  // programmatic step change (setStepperConfig → next()/previous()) keeps
  // the live region in sync with what the user visually sees.
  ngAfterViewInit() {
    this.updateStepAnnouncement();
    if (this.myStepper) {
      this.stepperSelectionSub = this.myStepper.selectionChange.subscribe(() => {
        this.updateStepAnnouncement();
      });
    }
  }

  ngOnDestroy() {
    this.stepperSelectionSub?.unsubscribe();
  }

  // set the classes that will determine how the stepper is displayed (step 1, 2, or 3);
  setStepperConfig(route: string): void {
    if (this.values.step2Routes.indexOf(route) > -1) {
      if(this.content.step2StatusComplete === true){
        this.myStepper.previous();
      }else if(this.content.step1StatusComplete === false){
        this.content.step1StatusComplete = true;
        this.myStepper.next();
      }
    }

    if (this.values.step3Routes.indexOf(route) > -1) {
      this.content.step2StatusComplete = true;
      this.myStepper.next();
    }

    if(this.commonValues.routes.orderConfirmation === route){
      this.content.step3StatusComplete = true;
      this.content.state = 'done';
    }

    //re-setting the step values to false when back button is clicked
    if(this.values.step1Routes.indexOf(route)>-1){
      this.content.step1StatusComplete = false;
      this.content.step2StatusComplete = false;
      this.content.step3StatusComplete = false;
    }

  }

  setStepperVisibility(route: string): void {
    // use split on the routes because we dont care about the route params being passed in;
    const hideStepperRoutes = [
      this.commonValues.routes.schoolSelect,
      this.commonValues.routes.schoolWelcome,
      this.commonValues.routes.schoolWithFiceCode.split('/')[0],
      this.commonValues.routes.messageWithReason.split('/')[0],
      this.commonValues.routes.outageMessage.split('/')[0]
    ];

    if (this.route) {
      this.show.stepper = hideStepperRoutes.indexOf(this.route.split('/')[0]) === -1;
    }
  }

  /**
   * WCAG 2.1 AA — 4.1.3 Status Messages.
   * Builds a human-readable progress string and pushes it into the template's
   * live region. The message only changes when the underlying step index
   * changes, so screen readers announce each transition exactly once.
   */
  private updateStepAnnouncement(): void {
    if (!this.show.stepper || !this.myStepper) {
      this.currentStepAnnouncement = '';
      return;
    }
    const steps = this.myStepper.steps.toArray();
    const idx = Math.max(0, Math.min(this.myStepper.selectedIndex, steps.length - 1));
    const label = this.stepTitles[idx] ?? '';
    this.currentStepAnnouncement = `Step ${idx + 1} of ${steps.length}: ${label}`;
  }
}
